import { cut, merge } from 'app-core/audio';
import _ from 'lodash';
import appConfig from 'app-core/config';
import { saveAs } from 'file-saver';
import { bufferToWave } from 'app-core/audio';

function getWordsForHash(words) {
	return words.map(f => ({
		key: f.key,
		w: f.SentenceWord,
		removed: f.removed,
		startOffset: f.startOffset,
		Offset: f.Offset,
		Duration: f.Duration
	}));
}

async function build(buffer, words, callback, opts) {
	if (__DEBUG__)
		console.debug('Build SonicScribe audio', { opts });

	let buffers = [];
	let offsets = [];

	let processedParts = [];

	let addRemovedPart = (start, end, w) => {
		let exists = processedParts.find(f => f.type === 'removed' && start <= f.end && end >= f.start);
		if (!exists) {
			exists = { start, end, words: [w], type: 'removed' };
			processedParts.push(exists);
		} else {
			exists.start = Math.min(start, exists.start);
			exists.end = Math.max(end, exists.end);
			if (!exists.words.includes(w)) {
				exists.words.push(w);
			}
		}

		if (__DEBUG__)
			console.debug(`addRemovedPart ${w?.SentenceWord} ${start} ${end}`, { exists });
	}

	/*
	let addRemovedPartVer2 = (offset, remove, w) => {
		let currentPos = offset;
		let size = Math.abs(remove);
		let direction = remove / size;

		if (__DEBUG__)
			console.debug(`addRemovedPart2 1 ${w.SentenceWord}`, { offset, remove });

		let counter = 0;
		while (size > 0 && currentPos > 0 && currentPos < buffer.duration && counter < 100) {
			counter++;
			let parts = processedParts.filter(f => f.type === 'removed' && (direction > 0 ? f.end > currentPos : f.start < currentPos));
			let part = direction > 0 ? parts[0] : parts.slice(-1)[0];

			if (!part) {
				if (direction > 0) {
					part = { start: currentPos, end: currentPos + size, words: [w], type: 'removed' };
				} else {
					part = { start: currentPos - size, end: currentPos, words: [w], type: 'removed' };
				}

				processedParts.push(part);
				size = 0;
			} else {
				let availableDist = Math.min(size, direction > 0 ? (part.start - currentPos) : (currentPos - part.start));

				if (availableDist > 0) {
					if (direction > 0) {
						part = { start: currentPos, end: currentPos + availableDist, words: [w], type: 'removed' };
					} else {
						part = { start: currentPos - availableDist, end: currentPos, words: [w], type: 'removed' };
					}
					processedParts.push(part);

					size -= availableDist;
				}

				currentPos = direction > 0 ? part.end : part.start;
			}

			if (__DEBUG__)
				console.debug(`addRemovedPart2 2 ${w.SentenceWord}`, { offset, remove, counter, ps: part.start, pe: part.end, diff: part.end - part.start });
		}
	}*/

	words = _.orderBy(words, f => f.CustomBuffer ? 0 : 1);
	
	for(let i = 0; i < words.length; i++) {
		let w = words[i];

		if (w.CustomBuffer) {
			if (!(w.removed || w.markToRemove)) {
				processedParts.push({ start: w.Offset, end: w.Offset + w.Duration, words: [w], type: 'added' });
			}
			let replacedWords = w.SourceWordsKeys.map(f => words.find(sw => sw.key === f));
			let removeStart = replacedWords[0].Offset - (replacedWords[0].startOffset || 0);
			let removeEnd = replacedWords.slice(-1)[0].Offset + replacedWords.slice(-1)[0].Duration;
			if (w.startOffset) {
				removeStart -= w.startOffset;
			}
			addRemovedPart(removeStart, removeEnd, w);
		} else {
			if (!w.replaced) {
				if (w.removed || w.markToRemove) {
					addRemovedPart(w.Offset, w.Offset + w.Duration, w);
				}
				if (w.startOffset) {
					addRemovedPart(w.Offset - w.startOffset, w.Offset, w);
				}
			}
		}
	}

	if (__DEBUG__)
		console.debug('Custom', { processedParts });

	for(let i = 0; i < words.length; i++) {
		let w = words[i];
		if (!w.removed) {
			if (w.CustomBuffer) {
				let deltaTotal = processedParts.filter(p => p.start <= w.Offset && (p.type !== 'added' || p.words[0] !== w)).reduce((r, p) => r + (p.type === 'removed' ? (p.start - p.end) : (p.end - p.start)), 0);
				w.actualStart = w.Offset + deltaTotal;
			} else {
				let deltaTotal = processedParts.filter(p => p.start <= w.Offset).reduce((r, p) => r + (p.type === 'removed' ? (p.start - p.end) : (p.end - p.start)), 0);
				w.actualStart = w.Offset + deltaTotal;
			}
		}
	}
	
	if (processedParts.length) {
		let lastPartEnd = 0;
		let parts = _.orderBy(processedParts, f => f.start);

		let processCut = async (start, end) => {
			try {			
				buffers.push(await cut(buffer, lastPartEnd, end));
				offsets.push(0);
			} catch(ex) {
				console.error(ex);
			}
		}

		for (let i = 0; i < parts.length; i++) {
			let part = parts[i];

			if (__DEBUG__)
				console.debug('Process part', { part, lastPartEnd });

			if (part.type === 'removed') {
				if (__DEBUG__)
					console.debug(`Process cut ${lastPartEnd} - ${part.start}`);
				if (part.start > lastPartEnd) {
					if (await processCut(lastPartEnd, part.start)) break;

					if (callback) {
						callback(buffers.slice(-1)[0], offsets.slice(-1)[0], lastPartEnd, part.start, buffers.length);
					}
				}
				lastPartEnd = Math.max(0, Math.max(lastPartEnd, part.end));
			} else {
				let w = part.words[0];

				buffers.push(w.CustomBuffer);
				offsets.push(Math.min(w.CustomBuffer.duration / 20, 0.1));

				if (callback) {
					callback(w.CustomBuffer, Math.min(w.CustomBuffer.duration / 20, 0.1), w.SourceWord?.Offset, w.SourceWord?.Offset + w.SourceWord?.Duration, buffers.length, 'overdub', w.Duration - w.SourceWord?.Duration);
				}
			}
		}

		if (lastPartEnd < buffer.duration) {
			await processCut(lastPartEnd, buffer.duration);
		}
	}

	if (__DEBUG__)
		console.debug('Process', { processedParts, buffers })

	let mergedBuffer = buffers.length === 1 ? buffers[0] : (buffers.length > 0 ? await merge(buffers, offsets) : buffer);
	let totalDuration = mergedBuffer.duration;
	let totalWords = words.filter(w => !(w.removed || w.markToRemove)).length;

	if (opts?.startOffset) {
		mergedBuffer = await cut(mergedBuffer, opts?.startOffset, opts?.startOffset + (opts?.duration || mergedBuffer.duration));
	} else if (opts?.duration && totalDuration > opts?.duration) {
		mergedBuffer = await cut(mergedBuffer, 0, opts?.duration);
	}

	return {
		buffer: mergedBuffer,
		totalDuration,
		totalWords,
		offsets
	};
}

async function initStructureSentences(structure, audioContext) {	
	let sentences = _.cloneDeep(structure) || [];
	let words = await prepareWords(sentences, audioContext);

	return {
		words,
		sentences
	}
}

async function prepareWords(structure, audioContext) {
	let app = appConfig.getVueApp();

	let promises = [];
	let words = [];
	structure.map((f, idx) => {
		f.words.map((word, wIdx) => {
			let sentence = f;
			if (!word.sentence) {
				Object.defineProperty(word, 'sentence', {
					get: () => sentence
				});
			}
			words.push(word);

			if (word.AudioFile) {
				app.$set(word, 'isProcessing', true);
				promises.push(app.$azure.getFileAudioBlob(word.AudioFile, true).catch(() => null).then(async r => {
					if (__DEBUG__)
						console.debug('Word audio', { r, file: word.AudioFile });
					let buffer = await app.$Data.getAudioBufferFromBlob(r, audioContext);
					Object.defineProperty(word, 'CustomBuffer', {
						get: () => buffer
					})
				}).finally(() => app.$set(word, 'isProcessing', false)));
			}
		});
		f.index = idx;
	});

	if (promises.length) {
		await Promise.all(promises);
	}

	return words;
}

async function initGroups(groups, audioContext) {
	let items = _.orderBy((groups?.items || []), f => f.offset);
	if (items.length) {
		await Promise.all(items.map(f => buildSonicScribeGroup(f, audioContext)));

		for(let i = 0; i < items.length; i++) {
			if (i > 0 && !items[i - 1].duration) {
				items[i - 1].duration = items[i].offset - items[i-1].offset;
			}
		}
	}

	if (__DEBUG__)
		console.debug('initGroups', { items });
}

async function buildSonicScribeGroup(group, audioContext) {
	if (!group.words) {
		if (__DEBUG__)
				console.debug('buildSonicScribeGroup', { str: group?.structure, group });
		let words = await prepareWords(group.structure, audioContext);
		Object.defineProperty(group, 'words', {
			get: () => words
		});
	}

	initializeSonicScribeGroup(group);
}

async function initializeSonicScribeGroup(group) {
	group.structure.map(f => {
		if (!f.sentence && !f.group) {
			Object.defineProperty(f, 'group', {
				get: () => group
			})
		}
	});

	group.words.map(f => initializeSonicScribeGroupWord(group, f));

	return group;
}

function initializeSonicScribeGroupWord(group, word) {
	if (word.startOffset === void 0) {
		word.startOffset = 0;
	}

	if (!word.group) {
		Object.defineProperty(word, 'group', {
			get: () => group
		})

		Object.defineProperty(word, 'groupKey', {
			get: () => `${word.key}_${group.id}`
		})

		Object.defineProperty(word, 'timelineStart', {
			get: () => word.actualStart - (group.startOffset || 0) + (group.offset || 0)
		})
	}
}

async function buildGroups(buffer, initedGroups) {
	let totalDuration = 0;
	let totalWords = 0;

	if (!initedGroups?.length) return {
		buffer,
		totalDuration: buffer.duration,
		totalWords: 0
	}

	let builtBuffers = await Promise.all(initedGroups.map(f => build(buffer, f.words, void 0, {
		startOffset: f.startOffset,
		duration: f.duration
	})));

	if (__DEBUG__)
		console.debug('Process buildGroups', { builtBuffers })
		
	let mergedBuffer = builtBuffers.length > 1 ? (await merge(builtBuffers.map(f => f.buffer))) : builtBuffers[0]?.buffer;
	totalDuration = builtBuffers.reduce((t, r) => t + r.totalDuration, 0);
	totalWords = builtBuffers.reduce((t, r) => t + r.totalWords, 0);

	return {
		buffer: mergedBuffer,
		totalDuration,
		totalWords
	};
}

function getSonicScribeGroupByTime(time, groups) {
	groups = _.orderBy(groups, f => f.offset);
	let group = groups.find(f => f.duration && (f.offset + f.duration || 0) > time);
	if (!group) {
		group = groups.find(f => !f.duration && f.offset < time);
	}

	return group;
}

export default {
	data() {
		return {
			
		}
	},
	computed: {
		isSonicScribe() {
			return this.creative?.JsonConfig?.isSonicScribe;
		},
		sonicScribeLanguage: {
			get: function() {
				return this.sonicScribeInfo('language') || 'en-US';
			},
			set: function(v) {
				this.sonicScribeInfo('language', v || null);
			}
		},
		isDebug() {
			return this.$route?.query?.debug || false;
		},
		sonicScrbieSTTProfile: {
			get: function() {
				return this.sonicScribeInfo('sttProfile') || 'SonicScribe';
			},
			set: function(v) {
				this.sonicScribeInfo('sttProfile', v || null);
			}
		},
	},
	mounted() {
		
	},
	destroyed() {
		
	},
	methods: {		
		async buildSonicScribe(creative, track, buffer) {
			if (__DEBUG__)
				console.debug('Build SonicScribe');

			let groups = creative?.JsonConfig?.sonicScribe?.groups;
			if (groups) {
				await initGroups(groups, track.__tracker.audioContext);
				let audio = await buildGroups(buffer, groups);
				return audio?.buffer || buffer;
			} else {			
				let structure = track?.insertion?.JsonConfig?.sonicScribe?.structure;
				if (structure) {
					let info = await initStructureSentences(structure, track.__tracker.audioContext);
					let audio = await build(buffer, info.words);
					return audio?.buffer || buffer;
				}
			}

			return buffer;
		},
		sonicScribeInfo(prop, value) {
			if (!this.creative?.JsonConfig) return;

			if (!this.creative?.JsonConfig?.sonicScribe) {
				this.$set(this.creative?.JsonConfig, 'sonicScribe', {});
			}
			if (value !== void 0) {
				this.$set(this.creative.JsonConfig.sonicScribe, prop, value);
			}

			return this.creative.JsonConfig.sonicScribe[prop];
		},
		getSTTProfiles(pageIndex, limit, opts) {
			if (opts?.byKey) {
				return this.$Data.getExternalApi('SpeechProcessingProfile/Get', `code=${opts?.byKey}`).then(r => ({ Results: [r] }));
			}
			return this.$Data.getExternalApi('SpeechProcessingProfile/List', `page=${pageIndex + 1}`, limit)
		},
		getSonicScribeGroupByTime(time) {
			let groups = _.orderBy(this.groups, f => f.offset);
			let group = groups.find(f => f.duration && (f.offset + f.duration || 0) > time);
			if (!group) {
				group = groups.find(f => !f.duration && f.offset < time);
			}

			return group;
		},
		async sonicScribeSplitGroup(time) {
			let group = getSonicScribeGroupByTime(time, this.groups);
			if (!group) return;

			let splittingGroupDuration = group?.duration;

			if (__DEBUG__)
				console.debug('sonicScribeSplitGroup', { group, time });

			this.$set(group, 'duration', time - group.offset);

			let source = group?.source;
			if (source) {
				this.$set(source, 'duration', time - group.offset);
			}

			let newGroup = {
				id: this.$Data.uuid(),
				offset: time,
				structure: _.cloneDeep(group.structure),
				startOffset: time + ((group.startOffset || 0) - group.offset)
			}

			if (splittingGroupDuration) {
				newGroup.duration = splittingGroupDuration - (time - group.offset)
			}

			await buildSonicScribeGroup(newGroup);

			let sGroupIdx = this.sonicScribeInfo('groups').items.findIndex(f => f.id === group.id);
			this.sonicScribeInfo('groups').items.splice(sGroupIdx + 1, 0, newGroup);
		}
	}
};

export {
	build,
	buildGroups,
	initGroups,
	initStructureSentences,
	getWordsForHash,
	initializeSonicScribeGroup,
	initializeSonicScribeGroupWord
}